import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateBranch, useGetBranchesList } from "../api/organization";
import { Branch } from "../types";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Icons } from "../assets/images/icons";
import { AppButton } from "../components/ui/AppButton";
import { DataTable } from "../components/ui/data-table";
import WithAuth from "../hoc/WithAuth";
import { BranchFormDialog } from "../components/branch/BranchFormDialog";
import { message } from "antd";
import { useTablePrint } from "../hooks/useTablePrint";
import { ExportButton } from "../components/ui/ExportButton";

function BranchesPage() {
  const navigate = useNavigate();

  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [open, setOpen] = useState(false);

  /** API */
  const { data: branches, isLoading } = useGetBranchesList({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });
  const { handlePrint } = useTablePrint();
  const { mutateAsync: createBranch, isPending } = useCreateBranch();

  /** Transform API data */
  const transformedBranches = useMemo((): Branch[] => {
    if (!branches?.data?.content) return [];

    return branches.data.content.map((item: any) => ({
      id: item.id,
      name: item.name,
      shortName: item.shortName,
      email: item.email,
      phoneNumber: item.phoneNumber,
    }));
  }, [branches]);

  // TODO: Event handlers
  // const handleEditBranch = (branch: Branch) => {
  //   navigate(`/edit-branch/${branch.id}`);
  // };

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleRowClick = (branch: Branch) => {
    navigate(`/branches/${branch.id}`);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleBranchSubmit = async (data: any) => {
    try {
      // Transform form data to match the backend DTO
      const submitData = {
        name: data.branchName,
        email: data.branchEmail,
        phoneNumber: data.branchPhone,
        shortName: data.shortName || "branch",
        type: data.type || "OFF_TAKERS",
        reInsurerEmail: data.reInsurerEmail || "",
        reInsurerName: data.reInsurerName || "",

        // Address is a stringified object (or you can join fields)
        address: JSON.stringify({
          region: data.region,
          city: data.city,
          subCity: data.subCity,
          woreda: data.woreda,
        }),

        // Contact person mapped from individual contact fields
        contactPerson: {
          fullName: data.contactFullName,
          username: data.contactUsername,
          email: data.contactEmail,
          phoneNumber: data.contactPhone,
          password: data.contactPassword || "123456",
        },
      };

      const result = await createBranch(submitData);

      if (result?.data?.status === "success") {
        message.success("Branch created successfully.");
        setOpen(false);
      } else {
        message.error("Operation failed");
        setOpen(false);
      }
    } catch (err: any) {
      console.error("Branch creation error:", err);
      message.error("Operation failed");
      setOpen(false);
    }
  };

  // Define columns for the DataTable
  const branchColumns = [
    {
      key: "name",
      title: "Branch Name",
      dataIndex: "name",
      align: "left" as const,
    },
    {
      key: "shortName",
      title: "Short Name",
      ataIndex: "shortName",
      align: "left" as const,
    },
    {
      key: "email",
      title: "Email",
      dataIndex: "email",
      align: "left" as const,
    },
    {
      key: "phoneNumber",
      title: "Phone Number",
      dataIndex: "phoneNumber",
      align: "left" as const,
    },
    {
      key: "actions",
      title: "Actions",
      align: "center" as const,
      render: (_: any, record: Branch) => (
        <Button
          variant="ghost"
          className="w-full justify-center text-sm text-gray-600"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(record);
          }}
        >
          View Detail
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between">
        {/* Search - Desktop */}
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search branches by name"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(branchColumns, transformedBranches, "Branch Report")
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={branchColumns}
            data={transformedBranches}
            fileName="branches"
            title="Branch Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: branches?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />

          {/** Create Branch Button & Dialog */}
          <BranchFormDialog
            mode="create"
            onSubmit={handleBranchSubmit}
            open={open}
            setOpen={setOpen}
            trigger={
              <AppButton
                customIcon={Icons.addCircle}
                variant="success"
                isLoading={isPending}
                disabled={isPending}
              >
                Create Branch
              </AppButton>
            }
          />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className=" hidden md:block">
        <DataTable<Branch>
          data={transformedBranches}
          columns={branchColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: branches?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          onRowClick={handleRowClick}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No item found"
          rowKey="id"
        />
      </div>
    </>
  );
}

export default WithAuth(BranchesPage);
